import { useState } from "react";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import useAPIError from "@services/useApiError";
import { UserStatus } from "@store/generalTypes";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { object, SchemaOf, string } from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LBtn } from "../../../common/components/LBtn";
import { InputFieldProps, LInputField } from "../../../common/components/LInputField";
import { LInputRow } from "../../../common/components/LInputRow";
import { NavItems } from "../../../common/helpers/navigation";
import { updateUser, User } from "../../../common/store/userInfoSlice";
import { useApi } from "../../../services/useApi";
import { SignupContainer } from "../components/SignupContainer";
import { TitleSelectOptions } from "../components/TitleSelectOptions";
import { LModal } from "@components";

const formSchema: SchemaOf<Omit<User, "id" | "status">> = object({
    email: string().email().required("* required"),
    firstName: string().required("* required"),
    lastName: string().required("* required"),
    title: string().optional(),
    jobTitle: string().required("* required"),
    otherJobTitle: string().when("jobTitle", (val, schema) => {
        return val === "Other" ? schema.required("Other job title is required") : schema;
    }),
    phoneNumber: string()
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number"))
        .optional(),
    password: string()
        .required("* required")
        .min(8, "The password must be at least 8 characters")
        .test("isValidPass", "Your password must contain uppercase, lowercase and at least a number", (value) => {
            const hasUpperCase = /[A-Z]/.test(value!);
            const hasLowerCase = /[a-z]/.test(value!);
            const hasNumber = /[0-9]/.test(value!);
            let validConditions = 0;
            const numberOfMustBeValidConditions = 3;
            const conditions = [hasLowerCase, hasUpperCase, hasNumber];
            conditions.forEach((condition) => (condition ? validConditions++ : null));
            if (validConditions >= numberOfMustBeValidConditions) {
                return true;
            }
            return false;
        })
});

export const SignupMainContactInfo = (): JSX.Element => {
    const [showExistUser, setShowExistUser] = useState(false);
    const { addError } = useAPIError();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const validateMethods = useForm<User>({
        resolver: yupResolver(formSchema)
    });

    const { createUser, loading } = useApi();

    const user = useAppSelector((state) => state.user);

    const formSubmitHandler: SubmitHandler<User> = async (data: User) => {
        data.status = UserStatus.Active;
        await createUser(data)
            .then((resp) => {
                if (resp.status === UserStatus.AlreadyExist) {
                    setShowExistUser(true);
                } else {
                    dispatch(updateUser(resp));

                    navigate(NavItems.Signup.OrgInfo.path);
                }
            })
            .catch((resp) => {
                addError(resp.response.data.detail, resp.response.data.title);
            });
    };

    const handleOnLoginClick = (): void => {
        navigate(NavItems.Login.Landing.path);
    };

    const nameGroup = [
        {
            type: "text",
            label: "Title",
            inputName: "title",
            isSmall: true,
            defaultValue: user.title,
            value: user.title
        } as InputFieldProps,
        {
            type: "text",
            label: "First Name",
            inputName: "firstName",
            placeholder: "e.g. John",
            isExpanded: true,
            defaultValue: user.firstName,
            value: user.firstName
        } as InputFieldProps,
        {
            type: "text",
            label: "Last Name",
            inputName: "lastName",
            placeholder: "e.g. Smith",
            isExpanded: true,
            defaultValue: user.lastName,
            value: user.lastName
        } as InputFieldProps
    ];

    return (
        <FormProvider {...validateMethods}>
            <SignupContainer
                title="Main Contact Information"
                subTitle="Please enter the main contact information for your organization"
            >
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <LInputField
                        type="email"
                        label="Your organizational email address"
                        inputName="email"
                        placeholder="e.g. alex@example.com"
                        defaultValue={user.email}
                    />
                    <LInputField label="Phone number" inputName="phoneNumber" defaultValue={""} />
                    <LInputRow inputs={nameGroup} />
                    <TitleSelectOptions value={user.jobTitle} otherJobTitle={user.otherJobTitle} />
                    <LInputField
                        type="password"
                        label="Create a password"
                        inputName="password"
                        placeholder="********"
                    />
                    <LBtn fullWidth={true} type="submit" loading={loading}>
                        {"Next"}
                    </LBtn>
                </form>
            </SignupContainer>
            {showExistUser && (
                <LModal
                    title={<p className="has-text-centered has-text-weight-bold is-size-3">Account Already Exists</p>}
                    fullHeight={false}
                    width={"s"}
                    hasCancelConfirmButtons
                    confirmText={"Login"}
                    cancelText=""
                    onConfirm={handleOnLoginClick}
                    onClose={() => {}}
                    divider={true}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered is-size-4">
                            {"An account with your email already exists. Please go to the login page to access "}
                            <strong>
                                LASSI-D<sup>™</sup>
                            </strong>
                            {". If you've forgotten your password, you can reset it on the login page."}
                        </p>
                    </div>
                </LModal>
            )}
        </FormProvider>
    );
};
